// The scene behind the board. Pixel scenes scale by a whole number so every
// pixel is the same size. A theme with high-definition backgrounds shows its
// picture instead, crossfading when it changes; a picture that cannot load
// gives way to the drawn scene. Over either sit the theme's ambient effects,
// each on its own layer, reading the picture under them. Animation pauses when
// the tab is hidden, and with motion off (or for people who ask their system
// for less motion) the effects go altogether and the scene holds still.

import { useEffect, useMemo, useRef, useState } from "react";
import type { Backdrop as BackdropSpec, ThemeBackground } from "../../shared/types";
import { useCamera } from "../canvas/camera";
import { cx } from "../lib/util";
import { backgroundUrl, pickBackground } from "./backgrounds";
import { EffectLayer } from "./EffectLayer";
import { effectsOf, mapPicture, type PictureMap } from "./effects";
import { sceneOf } from "./sceneOf";
import type { Scene } from "./scenes";
import type { ThemeDef } from "./themes";

const MARGIN = 40; // room for the parallax drift

/** Runs a scene on a canvas: sized to the window, drawn once, animated when allowed. */
function useSceneCanvas(canvas: React.RefObject<HTMLCanvasElement | null>, scene: Scene | null, key: string, motion: boolean, opaque: boolean, clearTop = false) {
  useEffect(() => {
    const el = canvas.current;
    if (!el || !scene) return;
    const ctx = el.getContext("2d", { alpha: !opaque })!;
    let W = 0;
    let H = 0;
    let state: Record<string, unknown> = {};
    let raf = 0;
    let last = 0;
    const t0 = performance.now();
    const setup = () => {
      const vw = window.innerWidth + MARGIN * 2;
      const vh = window.innerHeight + MARGIN * 2;
      if (scene.pixel) {
        const scale = Math.max(2, Math.round(window.innerHeight / 205));
        W = Math.ceil(vw / scale);
        H = Math.ceil(vh / scale);
        el.width = W;
        el.height = H;
        el.style.width = `${W * scale}px`;
        el.style.height = `${H * scale}px`;
      } else {
        const dpr = Math.min(window.devicePixelRatio || 1, vw * vh > 3_000_000 ? 1 : 1.5);
        W = Math.round(vw * dpr);
        H = Math.round(vh * dpr);
        el.width = W;
        el.height = H;
        el.style.width = `${vw}px`;
        el.style.height = `${vh}px`;
      }
      ctx.imageSmoothingEnabled = !scene.pixel;
      state = scene.build(W, H, { clearTop });
    };
    const draw = (now: number) => scene.frame({ ctx, w: W, h: H, t: (now - t0) / 1000 }, state);
    setup();
    draw(performance.now());
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const animate = motion && scene.fps > 0 && !reduce;
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      if (document.hidden) return;
      if (now - last < 1000 / scene.fps) return;
      last = now;
      draw(now);
    };
    if (animate) raf = requestAnimationFrame(loop);
    let rt: ReturnType<typeof setTimeout> | null = null;
    const onResize = () => {
      if (rt) clearTimeout(rt);
      rt = setTimeout(() => {
        setup();
        draw(performance.now());
      }, 120);
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      if (rt) clearTimeout(rt);
    };
  }, [key, motion, !!scene, clearTop]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** The picture, crossfading from the one before once the new one has loaded. Pixel art keeps every pixel sharp. */
function Photo({ pic, pixel, onFail }: { pic: ThemeBackground; pixel?: boolean; onFail: (key: string) => void }) {
  const [layers, setLayers] = useState<{ key: string; in: boolean }[]>([]);
  useEffect(() => {
    // the picture asked for goes on top: a new one fades in, one still in the stack comes back up
    setLayers((ls) => [...ls.filter((l) => l.key !== pic.key).slice(-1), ls.find((l) => l.key === pic.key) ?? { key: pic.key, in: false }]);
    // once it is showing, the ones under it go
    const t = setTimeout(() => setLayers((ls) => (ls.length > 1 && ls[ls.length - 1].key === pic.key && ls[ls.length - 1].in ? ls.slice(-1) : ls)), 900);
    return () => clearTimeout(t);
  }, [pic.key]);
  return (
    <>
      {layers.map((l) => (
        <img
          key={l.key}
          className={cx("backdrop-photo", l.in && "is-in", pixel && "is-pixel")}
          src={backgroundUrl(l.key)}
          alt=""
          decoding="async"
          draggable={false}
          onLoad={() => {
            setLayers((ls) => ls.map((x) => (x.key === l.key ? { ...x, in: true } : x)));
            // once it has faded in, what is under it can go, but only while it is still the one on top
            setTimeout(() => setLayers((ls) => (ls.length > 1 && ls[ls.length - 1].key === l.key ? ls.slice(-1) : ls)), 900);
          }}
          onError={() => onFail(l.key)}
        />
      ))}
    </>
  );
}

/**
 * The picture under the effects, measured for them: where its sky is dark and
 * its skyline is, where light falls on it. Measured again when the window
 * changes shape, since a covering picture is cropped differently.
 */
function usePictureMap(src: string | null): PictureMap | null {
  const [map, setMap] = useState<{ src: string; map: PictureMap | null } | null>(null);
  useEffect(() => {
    if (!src) return;
    let live = true;
    // the layer runs MARGIN past each edge of the window, and the picture covers the layer
    const measure = () => void mapPicture(src, (window.innerWidth + MARGIN * 2) / (window.innerHeight + MARGIN * 2)).then((m) => live && setMap({ src, map: m }));
    measure();
    let rt: ReturnType<typeof setTimeout> | null = null;
    const onResize = () => {
      if (rt) clearTimeout(rt);
      rt = setTimeout(measure, 400);
    };
    window.addEventListener("resize", onResize);
    return () => {
      live = false;
      window.removeEventListener("resize", onResize);
      if (rt) clearTimeout(rt);
    };
  }, [src]);
  return src && map?.src === src ? map.map : null;
}

export function Backdrop({ def, motion, dim, custom, parallax = true, pick, clearTop = false }: { def: ThemeDef; motion: boolean; dim: number; custom?: BackdropSpec | null; parallax?: boolean; pick?: number | null; clearTop?: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [broken, setBroken] = useState<string[]>([]);
  const own = !custom || custom.kind === "scene";
  const pics = (def.custom?.backgrounds ?? []).filter((p) => !broken.includes(p.key));
  const pic = own ? pickBackground(pics, pick) : null;
  const { key, scene } = sceneOf(def);
  // the theme's own effects belong to its own picture or scene, not to a backdrop someone chose instead
  const effects = useMemo(() => (own && motion ? effectsOf(def, !!pic) : []), [own, motion, def, !!pic]); // eslint-disable-line react-hooks/exhaustive-deps
  const drift = effects.find((e) => e.kind === "drift");
  const map = usePictureMap(pic && effects.some((e) => e.kind !== "drift") ? backgroundUrl(pic.key) : null);
  useSceneCanvas(canvas, own && !pic ? scene : null, key, motion, true, clearTop);

  // a little parallax: the scene drifts a few pixels as the board pans
  useEffect(() => {
    if (!parallax || !motion) {
      if (wrap.current) wrap.current.style.transform = "";
      return;
    }
    return useCamera.subscribe((c) => {
      const el = wrap.current;
      if (!el) return;
      const dx = Math.max(-MARGIN, Math.min(MARGIN, c.x * 0.012));
      const dy = Math.max(-MARGIN, Math.min(MARGIN, c.y * 0.012));
      el.style.transform = `translate3d(${dx.toFixed(1)}px, ${dy.toFixed(1)}px, 0)`;
    });
  }, [parallax, motion]);

  return (
    <div className="backdrop" aria-hidden>
      <div className="backdrop-layer" ref={wrap}>
        <div className={cx("backdrop-move", drift && "is-drift")} style={drift ? ({ "--drift": String(0.02 + (drift.strength ?? 0.5) * 0.07) } as React.CSSProperties) : undefined}>
          {own && !pic && <canvas ref={canvas} className={scene.pixel ? "is-pixel" : undefined} />}
          {pic && (
            <>
              <div className="backdrop-fill" style={{ background: def.bg }} />
              <Photo pic={pic} pixel={!!def.custom?.pixel} onFail={(k) => setBroken((b) => (b.includes(k) ? b : [...b, k]))} />
            </>
          )}
          {effects.map((e) => (e.kind === "drift" ? null : <EffectLayer key={`${def.id}:${e.kind}:${pic?.key ?? "scene"}`} effect={e} map={map} />))}
        </div>
        {custom?.kind === "color" && <div className="backdrop-fill" style={{ background: custom.color }} />}
        {custom?.kind === "image" && <div className="backdrop-fill backdrop-image" style={{ backgroundImage: `url("${custom.url.replace(/"/g, "%22")}")` }} />}
      </div>
      {dim > 0 && <div className="backdrop-dim" style={{ opacity: dim }} />}
    </div>
  );
}
